import httpClient, { IRequest, IResponse } from '../../../apiService/httpClient';
import { ICountry, IProjectInfoForm, ISiteEnvLevel } from '../../pages/ProjectInfo/IProjectInfo';
import { IMacro1ProjectPutData, IMacro2ProjectPutData, IProjectMacro2Request, IUpdateProjectFromFile } from '../IApiRequest';
import {
    IBlobFileResponse,
    ICoordinateCountry,
    IDesigner,
    IMacro1ProjectGetData,
    IMacro1ProjectOutputData,
    IMacro2ProjectGetData,
    IMacro2ProjectOutputData,
    IPaginatedResponse,
    IProjectBaseResponse,
    IProjectMacro1Response,
    IProjectMacro2Response,
    IUpdateProjectFromFileResponse,
} from '../IApiResponse';

const BeMaccaferriStoreProvider = () => {
    let _baseURL = process.env.REACT_APP_BE_BASE_URL as string;

    const _createRequest = <D>(_pathname: string, _queryString?: string, _data?: D): IRequest => {
        return {
            url: _queryString ? _pathname + '?' + _queryString : _pathname,
            baseURL: _baseURL,
            data: _data,
        };
    };

    //project------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------

    const getCountryByCoordinates = async (latitude: number, longitude: number): Promise<ICoordinateCountry> => {
        const nominatimResponse = fetch(`https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&zoom=3&format=json&extratags=1`);

        try {
            // Check if the user's IP address is Italian
            // Usa ip-api.com per ottenere la posizione basata sull'IP
            const ipCountryResponse = await fetch('https://func-iplocation-prd-we-001.azurewebsites.net/api/IPGeolocationAndWhoisLookup');
            const ipCountryData = await ipCountryResponse.json();
            // Parse the ipGeolocation string into an object
            const ipGeolocationData = JSON.parse(ipCountryData.ipGeolocation);
            const ipCountry = ipGeolocationData.country_code;
            if (ipCountry === 'IT') {
                return {
                    extratags: { 'ISO3166-1:alpha2': 'IT' },
                } as ICoordinateCountry;
            } else {
                // Fetch the coordinate-based country information
                const coordinateCountry = (await (await nominatimResponse).json()) as ICoordinateCountry;
                return coordinateCountry;
            }
        } catch (error) {
            console.error('Error getting IP-based country:', error);
            const coordinateCountry = (await (await nominatimResponse).json()) as ICoordinateCountry;
            return coordinateCountry;
        }
    };

    const getProjectsAsync = async (pageNumber: number, pageSize: number): Promise<IResponse<IPaginatedResponse<IProjectBaseResponse>>> =>
        httpClient.getAsync(_createRequest(`v1/macroprojects?PageNumber=${pageNumber}&PageSize=${pageSize}`));

    const getProjectCountriesAsync = async (countryName?: string): Promise<IResponse<ICountry[]>> =>
        httpClient.getAsync(_createRequest(`v1/macroprojects/countries ${countryName ? '?countryName=${countryName}' : ''}`));

    const getProjectSiteEnvLevelsAsync = async (lang?: string): Promise<IResponse<ISiteEnvLevel[]>> => httpClient.getAsync(_createRequest(`v1/macroprojects/site-environment-levels?lang=${lang}`));
    const getProjectsByDesignerAsync = async (designer: number, pageNumber: number, pageSize: number): Promise<IResponse<IPaginatedResponse<IProjectBaseResponse>>> =>
        httpClient.getAsync(_createRequest(`Store/Designer/GetAllProjectsByDesigner/${designer}?PageNumber=${pageNumber}&PageSize=${pageSize}`));

    const getProjectBaseAsync = async (id: number): Promise<IResponse<IProjectBaseResponse>> => httpClient.getAsync(_createRequest(`Store/Company/GetProjectBase/${id}`));

    const projectAvailableByAuthorAsync = async (id: number, author: string): Promise<IResponse<boolean>> =>
        httpClient.getAsync(_createRequest(`Store/Company/ProjectAvailableByAuthor?projectId=${id}&author=${author}`));

    const getMacro1ProjectByIdAsync = async (id: number): Promise<IResponse<IProjectMacro1Response>> => httpClient.getAsync(_createRequest(`Store/Designer/GetMacro1ProjectById/${id}`));

    //const getMacro2ProjectByIdAsync = async (id: number): Promise<IResponse<IProjectMacro2Response>> => httpClient.getAsync(_createRequest(`Store/Designer/GetMacro1ProjectById/${id}`));

    //GET Macro1 Project
    const getMacro1ProjectAsync = async (id: string): Promise<IResponse<IMacro1ProjectGetData>> => httpClient.getAsync(_createRequest(`v1/macro1/${id}`));
    //PUT Macro1 Project
    const putMacro1ProjectAsync = async (projectData: IMacro1ProjectPutData): Promise<IResponse<IMacro1ProjectOutputData>> =>
        httpClient.putAsync(_createRequest(`v1/macro1/${projectData.id}`, '', projectData));

    //GET Macro2 Project
    const getMacro2ProjectAsync = async (id: string): Promise<IResponse<IMacro2ProjectGetData>> => httpClient.getAsync(_createRequest(`v1/macro2/${id}`));
    //PUT Macro2 Project
    const putMacro2ProjectAsync = async (projectData: IMacro2ProjectPutData): Promise<IResponse<IMacro2ProjectOutputData>> =>
        httpClient.putAsync(_createRequest(`v1/macro2/${projectData.id}`, '', projectData));

    const addProjectAsync = async (project: IProjectInfoForm): Promise<IResponse<IProjectBaseResponse>> => httpClient.postAsync(_createRequest('v1/macroprojects', '', project));

    const putProjectAsync = async (project: IProjectInfoForm): Promise<IResponse<IProjectBaseResponse>> => httpClient.putAsync(_createRequest('v1/macroprojects/' + project.projectId, '', project));

    const updateProjectAsync = async (project: IProjectMacro2Request): Promise<IResponse<IProjectMacro2Response>> => httpClient.putAsync(_createRequest('Store/Company/UpdateProjectCBP', '', project));

    const deleteProjectByIdAsync = async (id: number): Promise<IResponse<boolean>> => httpClient.deleteAsync(_createRequest(`v1/macroprojects/${id}`));

    const updateProjectFromFileAsync = async (payload: IUpdateProjectFromFile): Promise<IResponse<IUpdateProjectFromFileResponse>> =>
        httpClient.putAsync(_createRequest('v1/macroprojects/uploadProject', '', payload));

    //designer------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------
    const getDesignerByIdAsync = async (id: number): Promise<IResponse<IDesigner>> => httpClient.getAsync(_createRequest(`Store/Designer/GetDesigner/${id}`));

    const getDesignerByNameAsync = async (designerFirm: string): Promise<IResponse<IDesigner>> => httpClient.getAsync(_createRequest(`Store/Designer/GetDesignerByName/${designerFirm}`));

    const addDesignerAsync = async (designer: IDesigner): Promise<IResponse<boolean>> => httpClient.postAsync(_createRequest(`Store/Designer/AddDesigner`, '', designer));

    //blob------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------

    const getAllDocumentsAsync = async (): Promise<IResponse<IBlobFileResponse[]>> => httpClient.getAsync(_createRequest(`Store/Blob/GetAllDocuments`));

    const uploadDocumentAsync = async (file: FormData): Promise<IResponse<boolean>> => httpClient.putAsync(_createRequest('Store/Blob/UploadWithFilename', '', file));

    const deleteDocumentAsync = async (name: string): Promise<IResponse<boolean>> => httpClient.deleteAsync(_createRequest(`Store/Blob/Delete/${name}`));

    return {
        getProjectsAsync,
        getProjectCountriesAsync,
        getProjectSiteEnvLevelsAsync,
        getProjectsByDesignerAsync,
        projectAvailableByAuthorAsync,
        deleteProjectByIdAsync,
        getProjectBaseAsync,
        getMacro1ProjectByIdAsync,
        //getMacro2ProjectByIdAsync,
        getMacro1ProjectAsync,
        putMacro1ProjectAsync,
        getMacro2ProjectAsync,
        putMacro2ProjectAsync,
        addProjectAsync,
        putProjectAsync,
        updateProjectAsync,
        updateProjectFromFileAsync,
        getDesignerByIdAsync,
        getDesignerByNameAsync,
        addDesignerAsync,
        getAllDocumentsAsync,
        uploadDocumentAsync,
        deleteDocumentAsync,
        getCountryByCoordinates,
    };
};

export default BeMaccaferriStoreProvider();
